import type { FlowReport } from '#shared/types';
import { SectionShell } from './SectionShell';
import {
  BacklogCard,
  BookedReleasedCard,
  RequestsCard,
  SlowestCard,
  SpreadCard,
  UnmatchedCard,
} from './FlowCards';
import { isFlowEmpty } from './report.logic';
import { useAnalyticsFilters } from './useAnalyticsFilters';
import { useAnalyticsSection } from './useAnalyticsSection';

/**
 * From a doctor's request to a released report.
 *
 * The screen is arranged as the pipeline runs: what came in, how fast it moved,
 * what is still stuck. The two series on the first chart are the two ends of
 * that pipeline, and the space between them is the backlog.
 */
export function FlowPage() {
  const filters = useAnalyticsFilters('flow');
  const { data, loading, error } = useAnalyticsSection<FlowReport>('flow', filters);

  return (
    <SectionShell
      section="flow"
      filters={filters}
      data={data}
      loading={loading}
      error={error}
      metrics={data?.headline}
      empty={data !== undefined && isFlowEmpty(data)}
      emptyMessage="Nothing was released in this period, and nothing is currently open."
    >
      {data && (
        <div className="c-an__grid">
          <BookedReleasedCard registrations={data.registrations} throughput={data.throughput} />
          <SpreadCard
            title="How long it takes"
            subtitle="Registration to release, over reports released in this period"
            spread={data.total_turnaround}
          />
          <SpreadCard
            title="How long before somebody starts"
            subtitle="Registration to the first result being saved"
            spread={data.to_first_result}
          />
          <BacklogCard backlog={data.backlog} />
          <RequestsCard orders={data.orders} />
          {data.orders.unmatched.length > 0 && <UnmatchedCard unmatched={data.orders.unmatched} />}
          <SlowestCard slowest={data.slowest} />
        </div>
      )}
    </SectionShell>
  );
}
